import * as React from 'react';

import { calculateBalanceBoundaries } from '../../../utils/balances';
import { calculateChange } from '../../../utils/changeCalculations';
import { toCurrency } from '../../../utils/formatting';
import { type CalendarDateRange } from '../../DateRangePicker';
import Widget from '../../Widget';

import type { ColumnTable } from 'arquero';

export interface ITotalBalanceChangeRowProps {
  transactions: ColumnTable | null;
  currentBalance?: number;
  dateRange: CalendarDateRange;
  showSign?: boolean;
}

const TotalBalanceChangeRow = ({
  transactions,
  currentBalance,
  dateRange,
  showSign = true,
}: ITotalBalanceChangeRowProps) => {
  const { balanceAtStart, balanceAtEnd } = React.useMemo(
    () =>
      calculateBalanceBoundaries({
        transactions: transactions ?? null,
        currentBalance: currentBalance ?? 0,
        dateRange,
      }),
    [transactions, currentBalance, dateRange]
  );

  const changeResult = React.useMemo(() => {
    if (balanceAtEnd === undefined || balanceAtStart === undefined) {
      return null;
    }
    const result = calculateChange({
      current: balanceAtEnd,
      previous: balanceAtStart,
      increaseGood: true,
    });
    return result;
  }, [balanceAtEnd, balanceAtStart]);

  const formattedValue = React.useMemo(() => {
    if (!changeResult) {
      return '—';
    }
    return toCurrency(changeResult.difference, { showSign });
  }, [changeResult, showSign]);

  const valueClassName = React.useMemo(() => {
    if (!changeResult) {
      return '';
    }
    return changeResult.isGoodChange ? 'text-success-primary' : 'text-error-primary';
  }, [changeResult]);

  if (balanceAtEnd === undefined || balanceAtStart === undefined) {
    return null;
  }

  return <Widget.Row label="Total change" value={formattedValue} valueClassName={valueClassName} />;
};

export default TotalBalanceChangeRow;
